(() => {
  'use strict';

  const initFilters = (root) => {
    const form = root.querySelector('[data-cpa-filters]');
    const cards = Array.from(root.querySelectorAll('[data-cpa-card]'));
    const count = root.querySelector('[data-cpa-result-count]');
    const empty = root.querySelector('[data-cpa-filter-empty]');

    if (!form || cards.length === 0 || !count || form.dataset.cpaReady === 'true') return;
    form.dataset.cpaReady = 'true';

    const search = form.elements.namedItem('search');
    const education = form.elements.namedItem('education');
    const noSsnResidency = form.elements.namedItem('no_ssn_residency');

    const update = () => {
      const query = String(search?.value || '').trim().toLocaleLowerCase();
      const educationFilter = String(education?.value || 'any');
      const requiresNoSsnResidency = Boolean(noSsnResidency?.checked);
      let visible = 0;

      cards.forEach((card) => {
        const name = card.dataset.name || '';
        const educationTags = String(card.dataset.education || '').split(/\s+/).filter(Boolean);
        const hasNoSsnResidencyRequirement = card.dataset.noSsnResidency === 'true';

        const matchesSearch = !query || name.includes(query);
        const matchesEducation = educationFilter === 'any' || educationTags.includes(educationFilter);
        const matchesApplicationConstraints = !requiresNoSsnResidency || hasNoSsnResidencyRequirement;
        const matches = matchesSearch && matchesEducation && matchesApplicationConstraints;

        card.hidden = !matches;
        if (matches) visible += 1;
      });

      count.textContent = `${visible} jurisdiction${visible === 1 ? '' : 's'} shown`;
      if (empty) empty.hidden = visible !== 0;
    };

    form.addEventListener('input', update);
    form.addEventListener('change', update);
    form.addEventListener('reset', () => window.setTimeout(update, 0));
    update();
  };

  const initChecklist = (checklist) => {
    const form = checklist.querySelector('.cpa-req__checklist-form');
    const boxes = Array.from(checklist.querySelectorAll('[data-cpa-check]'));
    const items = Array.from(checklist.querySelectorAll('[data-cpa-check-item]'));
    const count = checklist.querySelector('[data-cpa-checklist-count]');
    const progress = checklist.querySelector('[data-cpa-checklist-progress]');

    if (!form || boxes.length === 0 || !count || !progress || checklist.dataset.cpaReady === 'true') return;
    checklist.dataset.cpaReady = 'true';

    const update = () => {
      const completed = boxes.filter((box) => box.checked).length;
      count.textContent = `${completed} of ${boxes.length} steps checked`;
      progress.max = boxes.length;
      progress.value = completed;

      items.forEach((item) => {
        const box = item.querySelector('[data-cpa-check]');
        item.classList.toggle('is-complete', Boolean(box?.checked));
      });
    };

    form.addEventListener('change', update);
    form.addEventListener('reset', () => window.setTimeout(update, 0));
    update();
  };

  const initFeeCalculator = (calculator) => {
    const root = calculator.closest('[data-cpa-requirements-hub]');
    const dataElement = root?.querySelector('[data-cpa-fee-data]');
    const form = calculator.querySelector('[data-cpa-fee-form]');
    const jurisdictionSelect = form?.elements.namedItem('jurisdiction');
    const sectionsSelect = form?.elements.namedItem('sections');
    const fieldset = calculator.querySelector('[data-cpa-fee-fieldset]');
    const optionsList = calculator.querySelector('[data-cpa-fee-options]');
    const totalOutput = calculator.querySelector('[data-cpa-fee-total]');
    const summary = calculator.querySelector('[data-cpa-fee-summary]');
    const sourceLink = calculator.querySelector('[data-cpa-fee-source]');
    const guideLink = calculator.querySelector('[data-cpa-fee-guide]');

    if (
      !dataElement ||
      !form ||
      !jurisdictionSelect ||
      !sectionsSelect ||
      !fieldset ||
      !optionsList ||
      !totalOutput ||
      !summary ||
      !sourceLink ||
      !guideLink ||
      calculator.dataset.cpaReady === 'true'
    ) return;

    let jurisdictions;
    try {
      const parsed = JSON.parse(dataElement.textContent || '[]');
      jurisdictions = new Map(parsed.map((jurisdiction) => [jurisdiction.code, jurisdiction]));
    } catch {
      summary.textContent = 'The fee data could not be loaded. Use the official fee schedule before paying.';
      return;
    }

    calculator.dataset.cpaReady = 'true';
    const currencyFormatters = new Map();

    const formatCurrency = (amount, currency = 'USD') => {
      if (!currencyFormatters.has(currency)) {
        currencyFormatters.set(currency, new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 2
        }));
      }
      return currencyFormatters.get(currency).format(amount);
    };

    const formatDate = (value) => {
      if (!value) return null;
      const date = new Date(`${value}T00:00:00`);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    };

    const setLink = (link, url) => {
      if (url) {
        link.href = url;
        link.hidden = false;
      } else {
        link.removeAttribute('href');
        link.hidden = true;
      }
    };

    const sections = () => Number.parseInt(sectionsSelect.value, 10) || 1;

    const feeValueLabel = (item) => {
      const amount = Number(item.amount);
      if (item.basis === 'percent') return `${amount}% of the applicable selected fees`;
      const value = formatCurrency(amount, item.currency || 'USD');
      const qualifiedValue = item.amount_status === 'approximate' ? `Approximately ${value}` : value;
      if (item.basis === 'per_section') return `${qualifiedValue} per section × ${sections()}`;
      if (item.basis === 'section_bundle') {
        return `${qualifiedValue} for ${item.section_count} section${item.section_count === 1 ? '' : 's'}`;
      }
      return qualifiedValue;
    };

    const selectedState = () => new Map(
      Array.from(optionsList.querySelectorAll('input[type="checkbox"]'))
        .map((input) => [input.value, input.checked])
    );

    const selectedJurisdiction = () => jurisdictions.get(String(jurisdictionSelect.value || ''));

    const isPartialEstimate = (jurisdiction) =>
      Number(jurisdiction?.modelVersion || 0) < 3 ||
      String(jurisdiction?.status || '').startsWith('partial_');

    const coverageMessage = (jurisdiction) => {
      if (Number(jurisdiction?.modelVersion || 0) < 3) {
        return 'This jurisdiction needs a canonical fee schedule before its estimate can be treated as complete.';
      }
      return jurisdiction.coverageNote || 'This is a partial estimate because the current testing-provider charge is not included.';
    };

    const updateTotal = () => {
      const jurisdiction = selectedJurisdiction();
      if (!jurisdiction) {
        totalOutput.textContent = formatCurrency(0);
        summary.textContent = 'Choose a jurisdiction to begin.';
        return;
      }

      const itemMap = new Map((jurisdiction.items || []).map((item) => [item.key, item]));
      const selectedItems = Array.from(optionsList.querySelectorAll('input[type="checkbox"]:checked'))
        .map((input) => itemMap.get(input.value))
        .filter(Boolean);
      const feeSubtotal = (item) => {
        const amount = Number(item.amount);
        if (!Number.isFinite(amount) || item.basis === 'percent') return 0;
        return item.basis === 'per_section' ? amount * sections() : amount;
      };
      const selectedBaseItems = selectedItems.filter((item) => item.basis !== 'percent');
      const subtotal = selectedBaseItems.reduce((total, item) => total + feeSubtotal(item), 0);
      const percentageItems = selectedItems.filter((item) => item.basis === 'percent');
      const percentageFees = percentageItems.reduce((total, item) => {
        const rate = Number(item.amount);
        if (!Number.isFinite(rate)) return total;
        const baseStages = Array.isArray(item.percentage_base_stages) ? item.percentage_base_stages : [];
        const percentageBase = baseStages.length
          ? selectedBaseItems
            .filter((baseItem) => baseStages.includes(baseItem.stage))
            .reduce((baseTotal, baseItem) => baseTotal + feeSubtotal(baseItem), 0)
          : subtotal;
        return total + (percentageBase * rate / 100);
      }, 0);
      const total = subtotal + percentageFees;
      const verifiedDate = formatDate(jurisdiction.verifiedDate);
      totalOutput.textContent = formatCurrency(total, jurisdiction.currency || 'USD');
      const coveragePrefix = isPartialEstimate(jurisdiction) ? 'Partial estimate: ' : '';
      summary.textContent = selectedItems.length
        ? `${coveragePrefix}${selectedItems.length} selected fee${selectedItems.length === 1 ? '' : 's'}${verifiedDate ? ` from the ${verifiedDate} source snapshot` : ''}. Change any checkbox that does not apply to you.`
        : `${coveragePrefix}No fees selected${verifiedDate ? ` from the ${verifiedDate} source snapshot` : ''}. Select the charges that apply to your situation.`;
    };

    const appendFeeOption = (item, checked, jurisdiction) => {
      const listItem = document.createElement('li');
      const input = document.createElement('input');
      const copy = document.createElement('div');
      const label = document.createElement('label');
      const value = document.createElement('span');
      const id = `CpaFee-${jurisdiction.code}-${item.key}`.replace(/[^a-zA-Z0-9_-]/g, '-');

      input.type = 'checkbox';
      input.id = id;
      input.name = 'fees';
      input.value = item.key;
      input.checked = checked;
      input.defaultChecked = checked;
      input.dataset.cpaFeeItem = '';
      label.htmlFor = id;
      label.textContent = item.label;
      value.className = 'cpa-req__fee-option-value';
      value.textContent = feeValueLabel(item);

      copy.append(label, value);
      if (item.notes) {
        const note = document.createElement('p');
        note.textContent = item.notes;
        copy.append(note);
      }
      if (!item.default_selected) {
        const condition = document.createElement('span');
        condition.className = 'cpa-req__fee-condition';
        condition.textContent = 'Add only if this applies';
        copy.append(condition);
      }
      listItem.append(input, copy);
      optionsList.append(listItem);
    };

    const renderSchedule = (preserveSelections = false) => {
      const jurisdiction = selectedJurisdiction();
      const previous = preserveSelections ? selectedState() : new Map();
      optionsList.replaceChildren();

      if (!jurisdiction) {
        const placeholder = document.createElement('li');
        placeholder.className = 'cpa-req__fee-placeholder';
        placeholder.textContent = 'Choose a jurisdiction to load its researched fee schedule.';
        optionsList.append(placeholder);
        fieldset.disabled = true;
        setLink(sourceLink, null);
        setLink(guideLink, null);
        updateTotal();
        return;
      }

      setLink(sourceLink, jurisdiction.sourceUrl);
      setLink(guideLink, jurisdiction.url);
      const items = (jurisdiction.items || []).filter((item) =>
        item.basis !== 'section_bundle' || Number(item.section_count) === sections()
      );

      if (isPartialEstimate(jurisdiction) && items.length) {
        const coverage = document.createElement('li');
        coverage.className = 'cpa-req__fee-placeholder';
        coverage.textContent = coverageMessage(jurisdiction);
        optionsList.append(coverage);
      }

      if (!items.length) {
        const unavailable = document.createElement('li');
        unavailable.className = 'cpa-req__fee-placeholder';
        if (Number(jurisdiction.modelVersion) < 3) {
          unavailable.textContent = 'This jurisdiction needs a canonical fee schedule before an estimate can be shown.';
        } else if (jurisdiction.status === 'not_accepting_applications') {
          unavailable.textContent = 'This jurisdiction is not currently accepting CPA exam applications, so there is no current application-fee estimate.';
        } else {
          unavailable.textContent = coverageMessage(jurisdiction);
        }
        optionsList.append(unavailable);
        fieldset.disabled = true;
      } else {
        items.forEach((item) => {
          const checked = previous.has(item.key)
            ? previous.get(item.key)
            : Boolean(item.default_selected);
          appendFeeOption(item, checked, jurisdiction);
        });
        fieldset.disabled = false;
      }

      updateTotal();
    };

    form.addEventListener('change', (event) => {
      if (event.target === jurisdictionSelect) {
        renderSchedule(false);
      } else if (event.target === sectionsSelect) {
        renderSchedule(true);
      } else if (event.target.matches('[data-cpa-fee-item]')) {
        updateTotal();
      }
    });
    form.addEventListener('reset', () => window.setTimeout(() => renderSchedule(false), 0));
    renderSchedule(false);
  };

  const init = () => {
    document.querySelectorAll('[data-cpa-requirements-hub]').forEach(initFilters);
    document.querySelectorAll('[data-cpa-fee-calculator]').forEach(initFeeCalculator);
    document.querySelectorAll('[data-cpa-checklist]').forEach(initChecklist);
  };

  init();
  document.addEventListener('shopify:section:load', init);
})();
